import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { FiUsers, FiUserCheck, FiUserX, FiBriefcase, FiCalendar, FiClock } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const Dashboard = () => {
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [data, setData] = useState(null);
  const [myLeaves, setMyLeaves] = useState([]);
  const [myAttendance, setMyAttendance] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isAdminHR) {
          const res = await api.get('/dashboard');
          setData(res.data);
        }

        // Employee-specific data
        const empId = user?.employeeId || user?._id;
        const promises = [
          api.get('/leaves').catch(() => ({ data: [] })),
          api.get('/attendance', { params: { month: new Date().getMonth() + 1, year: new Date().getFullYear() } }).catch(() => ({ data: [] })),
        ];
        if (empId) {
          promises.push(api.get(`/leaves/balance/${empId}`).catch(() => ({ data: null })));
        }

        const results = await Promise.all(promises);
        setMyLeaves(Array.isArray(results[0].data) ? results[0].data : []);
        setMyAttendance(Array.isArray(results[1].data) ? results[1].data : []);
        if (results[2]) setLeaveBalance(results[2].data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdminHR, user?.employeeId, user?._id]);

  if (loading) return <div className="loading">Loading dashboard...</div>;

  const pieData = data?.departmentData?.filter(d => d.count > 0) || [];

  // Employee Dashboard
  if (!isAdminHR) {
    const presentDays = myAttendance.filter(a => a.status === 'Present' || a.status === 'Late').length;
    const absentDays = myAttendance.filter(a => a.status === 'Absent').length;
    const pendingLeaves = myLeaves.filter(l => l.status === 'Pending').length;

    return (
      <div className="dashboard">
        <h1>Welcome, {user?.name}</h1>

        <div className="summary-cards">
          <div className="summary-card" style={{ borderLeft: '4px solid #10b981' }}>
            <div className="card-icon" style={{ color: '#10b981' }}><FiUserCheck /></div>
            <div><h3>{presentDays}</h3><p>Days Present (This Month)</p></div>
          </div>
          <div className="summary-card" style={{ borderLeft: '4px solid #ef4444' }}>
            <div className="card-icon" style={{ color: '#ef4444' }}><FiUserX /></div>
            <div><h3>{absentDays}</h3><p>Days Absent (This Month)</p></div>
          </div>
          <div className="summary-card" style={{ borderLeft: '4px solid #f59e0b' }}>
            <div className="card-icon" style={{ color: '#f59e0b' }}><FiCalendar /></div>
            <div><h3>{pendingLeaves}</h3><p>Pending Leave Requests</p></div>
          </div>
          <div className="summary-card" style={{ borderLeft: '4px solid #4f46e5' }}>
            <div className="card-icon" style={{ color: '#4f46e5' }}><FiClock /></div>
            <div><h3>{myAttendance.length}</h3><p>Total Records (This Month)</p></div>
          </div>
        </div>

        {leaveBalance && (
          <div className="chart-container" style={{ marginBottom: '1.5rem' }}>
            <h3>Leave Balance</h3>
            <div className="detail-grid" style={{ marginTop: '1rem' }}>
              {['sick', 'casual', 'annual'].map(type => (
                <div key={type} className="detail-item">
                  <label>{type.charAt(0).toUpperCase() + type.slice(1)} Leave</label>
                  <span>{leaveBalance[type] ?? '-'} days</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="chart-container">
          <h3>My Recent Leave Requests</h3>
          <table className="table">
            <thead><tr><th>Type</th><th>From</th><th>To</th><th>Reason</th><th>Status</th></tr></thead>
            <tbody>
              {myLeaves.map(l => (
                <tr key={l._id}>
                  <td>{l.leaveType}</td>
                  <td>{new Date(l.fromDate).toLocaleDateString()}</td>
                  <td>{new Date(l.toDate).toLocaleDateString()}</td>
                  <td>{l.reason}</td>
                  <td><span className={`status-badge status-${l.status.toLowerCase()}`}>{l.status}</span></td>
                </tr>
              ))}
              {myLeaves.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center' }}>No leave requests</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Admin / HR Dashboard
  const cards = [
    { title: 'Total Employees', value: data?.totalEmployees || 0, icon: <FiUsers />, color: '#4f46e5' },
    { title: 'Present Today', value: data?.presentToday || 0, icon: <FiUserCheck />, color: '#10b981' },
    { title: 'On Leave', value: data?.onLeave || 0, icon: <FiUserX />, color: '#f59e0b' },
    { title: 'Open Positions', value: data?.openPositions || 0, icon: <FiBriefcase />, color: '#ef4444' },
  ];

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>
      <div className="summary-cards">
        {cards.map((card) => (
          <div key={card.title} className="summary-card" style={{ borderLeft: `4px solid ${card.color}` }}>
            <div className="card-icon" style={{ color: card.color }}>{card.icon}</div>
            <div><h3>{card.value}</h3><p>{card.title}</p></div>
          </div>
        ))}
      </div>

      <div className="charts-row">
        <div className="chart-container">
          <h3>Department-wise Employees</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data?.departmentData || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#4f46e5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-container">
          <h3>Employee Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                {pieData.map((entry, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="chart-container" style={{ marginTop: '1.5rem' }}>
        <h3>Recent Leave Requests</h3>
        <table className="table">
          <thead><tr><th>Employee</th><th>Type</th><th>From</th><th>To</th><th>Status</th></tr></thead>
          <tbody>
            {(data?.recentLeaves || []).map((leave) => (
              <tr key={leave._id}>
                <td>{leave.employee?.name || 'N/A'}</td>
                <td>{leave.leaveType}</td>
                <td>{new Date(leave.fromDate).toLocaleDateString()}</td>
                <td>{new Date(leave.toDate).toLocaleDateString()}</td>
                <td><span className={`status-badge status-${leave.status.toLowerCase()}`}>{leave.status}</span></td>
              </tr>
            ))}
            {(!data?.recentLeaves || data.recentLeaves.length === 0) && (
              <tr><td colSpan="5" style={{ textAlign: 'center' }}>No recent leaves</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
